/**
 * Per-field-type validation for the new-signature form. Rules apply to BOTH
 * the per-source override inputs (prefilled-with-source) and the sender-typed
 * inputs (prefilled-no-source) — same field shape, same rules.
 *
 * Empty values are considered "valid" here: required-ness gating lives in the
 * existing missing-fields panel. These checks only fire when the realtor
 * actually typed something so they don't double up on the required asterisk.
 */
import type { SignatureTemplateField } from "../@types/signatures";
// The generic validators live in a shared util so the contact dialog (and any
// other feature) can reuse the SAME logic without duplicating it here.
import { isValidEmail, isValidNumber, isValidPhone, isValidIsraeliId } from "../utils/fieldValidators";

export { isValidEmail, isValidNumber, isValidPhone, isValidIsraeliId };

export type FieldValidationError =
  | "invalidEmail"
  | "invalidPhone"
  | "invalidNumber"
  | "invalidIdNumber"
  | "invalidOption";

const isIdNumberContext = (f: {
  prefillSource?: string | null;
  labelKey?: string | null;
}): boolean =>
  f.prefillSource === "contact.id_number" || f.labelKey === "contact.id_number";

const isPhoneContext = (f: SignatureTemplateField): boolean =>
  f.type === "phone" || !!f.prefillSource?.endsWith(".phone");

const isEmailContext = (f: SignatureTemplateField): boolean =>
  f.type === "email" || !!f.prefillSource?.endsWith(".email");

/**
 * Returns a stable error code (matches an i18n suffix under
 * `signatures.newDoc.validation`) or null if the value is acceptable.
 *
 * Order matters: id-number is checked before generic-text fallbacks so a
 * text field bound to `contact.id_number` validates as a T.Z, not as
 * unconstrained text.
 */
export const validateFieldValue = (
  field: SignatureTemplateField,
  value: string
): FieldValidationError | null => {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (isIdNumberContext(field)) {
    return isValidIsraeliId(v) ? null : "invalidIdNumber";
  }
  if (isEmailContext(field)) return isValidEmail(v) ? null : "invalidEmail";
  if (isPhoneContext(field)) return isValidPhone(v) ? null : "invalidPhone";
  if (field.type === "number") return isValidNumber(v) ? null : "invalidNumber";
  if (field.type === "radio") {
    const allowed = field.radioFieldConfig?.options.some((option) => option.value === v);
    return allowed ? null : "invalidOption";
  }
  return null;
};
